/**
 * BirthdayWall — interactive scrapbook birthday card (2026-06-21).
 *
 * Full-screen overlay. Teammates leave marker-signed sticky notes (public or
 * private-to-the-honoree); their profile avatar auto-drops on as a Polaroid.
 * Confetti + live signature counter. The honoree sees every note incl. private
 * ones; everyone else sees public notes + name-only cards for private ones.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toPng } from "html-to-image";
import { X as XIcon, Camera, Sparkle, DownloadSimple, Play } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ASSET = process.env.REACT_APP_BACKEND_URL;

const NOTE_TINTS = ["y", "p", "b", "g", "o", "w"];
const INK = ["c1", "c2", "c3", "c4", "c5", "c6"];
const STICKER_TRAY = ["🎂", "🎉", "🎈", "👑", "⭐️", "💛", "🥳", "🎁"];

// deterministic pick from a person's id so their note looks consistent
function pick(seedStr, arr) {
  let h = 0;
  for (let i = 0; i < (seedStr || "").length; i++) h = (h * 31 + seedStr.charCodeAt(i)) >>> 0;
  return arr[h % arr.length];
}
function tiltFor(seedStr) {
  let h = 0;
  for (let i = 0; i < (seedStr || "").length; i++) h = (h * 17 + seedStr.charCodeAt(i)) >>> 0;
  return ((h % 60) / 10 - 3).toFixed(2); // -3deg .. +3deg
}
function absUrl(u) {
  if (!u) return "";
  return u.startsWith("http") ? u : `${ASSET}${u}`;
}

// Robust slide image: iOS Safari frequently does NOT fire React's onLoad for
// cached/already-decoded images, which left the spinner stuck forever. We also
// check img.complete on mount (and after each paint) so a photo that is already
// decoded is marked ready immediately regardless of whether the event fired.
function SlidePhoto({ thumb, full, alt, onReady, testId }) {
  const ref = useRef(null);
  useEffect(() => {
    const img = ref.current;
    if (img && img.complete && img.naturalWidth > 0) onReady();
  });
  return (
    <img
      ref={ref}
      src={absUrl(thumb || full)}
      alt={alt}
      loading="eager"
      decoding="async"
      onLoad={onReady}
      onError={(e) => {
        const img = e.currentTarget;
        if (!img.dataset.fb && full && full !== thumb) { img.dataset.fb = "1"; img.src = absUrl(full); }
        else { onReady(); }
      }}
      data-testid={testId}
    />
  );
}

export default function BirthdayWall({ honoreeId, date, kind = "birthday", years = 0, honoreeName = "", user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [stickers, setStickers] = useState([]);
  const [sending, setSending] = useState(false);
  const [showSign, setShowSign] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [nameEditing, setNameEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [visibleCount, setVisibleCount] = useState(6);
  const moreRef = useRef(null);
  const confettiHost = useRef(null);
  const captureRef = useRef(null);
  const crewId = user?.crew_id;
  const isAnniv = kind === "anniversary";
  const [photos, setPhotos] = useState([]);
  const [slide, setSlide] = useState(0);
  const [loaded, setLoaded] = useState(() => new Set());
  const markLoaded = (id) => setLoaded((s) => (s.has(id) ? s : new Set(s).add(id)));
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [paused, setPaused] = useState(false);
  const photoInputRef = useRef(null);
  const touchX = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [playIdx, setPlayIdx] = useState(0);
  const [savingVideo, setSavingVideo] = useState(false);
  const [savingPhotos, setSavingPhotos] = useState(false);
  const [cleanView, setCleanView] = useState(false);
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/birthday/wall/${honoreeId}`, {
        params: { actor_id: crewId, date, kind },
      });
      setData(r.data);
      if (r.data?.already_signed) setShowSign(false);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (honoreeId && crewId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [honoreeId, crewId, date, kind]);

  // confetti burst on open
  useEffect(() => {
    const colors = ["#f43f5e", "#a855f7", "#22d3ee", "#facc15", "#34d399", "#fb923c", "#ec4899"];
    const host = confettiHost.current;
    if (!host) return undefined;
    let alive = true;
    const pop = () => {
      if (!alive || !host) return;
      const c = document.createElement("div");
      c.className = "bw-confetti";
      c.style.left = Math.random() * 100 + "%";
      c.style.background = colors[Math.floor(Math.random() * colors.length)];
      c.style.animationDuration = 2.6 + Math.random() * 2.2 + "s";
      if (Math.random() > 0.5) { c.style.borderRadius = "50%"; c.style.width = "8px"; c.style.height = "8px"; }
      host.appendChild(c);
      setTimeout(() => c.remove(), 5200);
    };
    for (let i = 0; i < 36; i++) setTimeout(pop, i * 90);
    const iv = setInterval(pop, 520);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // Same iOS standalone home-indicator fix as Login: paint <body> with the
  // card's brown background so the bottom safe-area strip doesn't leak the
  // app's gray body background behind this full-screen takeover card.
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#bd8a51";
    return () => { document.body.style.backgroundColor = prev; };
  }, []);

  // --- Journey slideshow (anniversary only): "how it started → now" ---
  const loadPhotos = async () => {
    if (!isAnniv) return;
    try {
      const r = await axios.get(`${API}/birthday/wall/${honoreeId}/photos`, { params: { date, kind } });
      setPhotos(r.data?.photos || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadPhotos(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [honoreeId, kind, date]);

  // Auto-advance, but only AFTER the current photo has actually loaded, so we
  // never flip to the next picture before the current one is on screen. Pauses
  // on hover/touch. Loops oldest → newest.
  useEffect(() => {
    if (!isAnniv || paused || photos.length < 2) return undefined;
    const cur = photos[slide];
    const ready = !cur || loaded.has(cur.id);
    const t = setTimeout(() => setSlide((i) => (i + 1) % photos.length), ready ? 3500 : 8000);
    return () => clearTimeout(t);
  }, [isAnniv, paused, photos.length, slide, loaded]);
  useEffect(() => { if (slide >= photos.length) setSlide(0); }, [photos.length, slide]);

  const goTo = (i) => { if (photos.length) setSlide(((i % photos.length) + photos.length) % photos.length); };
  // Only mount the current slide and its immediate neighbours so 60+ photos
  // don't spawn 60 simultaneous image requests (fixes slow/stuck loading).
  const near = (i, cur, len) => {
    if (len <= 3) return true;
    const d = Math.min(Math.abs(i - cur), len - Math.abs(i - cur));
    return d <= 1;
  };
  const canRemovePhoto = (p) => !!crewId && (p.added_by_id === crewId || isHonoree);

  const addPhotos = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type && f.type.startsWith("image/"));
    if (list.length === 0) return;
    setUploadingPhoto(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("actor_id", crewId || "");
        fd.append("date", date || "");
        fd.append("kind", kind);
        await axios.post(`${API}/birthday/wall/${honoreeId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await loadPhotos();
    } catch { /* ignore */ } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const removePhoto = async (pid) => {
    try {
      await axios.delete(`${API}/birthday/wall/photo/${pid}`, { params: { actor_id: crewId } });
      setPhotos((cur) => cur.filter((p) => p.id !== pid));
    } catch { /* ignore */ }
  };

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) goTo(dx < 0 ? slide + 1 : slide - 1);
    touchX.current = null;
  };

  // --- Cinematic full-screen play mode (Ken Burns + crossfades + music) ---
  const openPlay = () => { if (photos.length) { setPlayIdx(0); setPlaying(true); } };
  const closePlay = () => {
    setPlaying(false);
    const a = audioRef.current;
    if (a) { try { a.pause(); a.currentTime = 0; } catch { /* ignore */ } }
  };
  useEffect(() => {
    if (!playing || photos.length === 0) return undefined;
    const a = audioRef.current;
    if (a) { try { a.currentTime = 0; a.volume = 0.75; a.play().catch(() => {}); } catch { /* ignore */ } }
    return undefined;
  }, [playing, photos.length]);
  // Advance only once the current frame has loaded (prevents blank/early flips).
  useEffect(() => {
    if (!playing || photos.length < 2) return undefined;
    const cur = photos[playIdx];
    const ready = !cur || loaded.has(cur.id);
    const t = setTimeout(() => setPlayIdx((i) => (i + 1) % photos.length), ready ? 3200 : 7000);
    return () => clearTimeout(t);
  }, [playing, photos.length, playIdx, loaded]);

  // Save the montage as a shareable .mp4 — asks the server to render, polls
  // until ready, then downloads the file.
  const saveVideo = async () => {
    if (!photos.length || savingVideo) return;
    setSavingVideo(true);
    try {
      await axios.post(`${API}/birthday/wall/${honoreeId}/montage`, { actor_id: crewId, date, kind });
      let url = "";
      for (let n = 0; n < 48; n++) {
        const r = await axios.get(`${API}/birthday/wall/${honoreeId}/montage`, { params: { date, kind } });
        if (r.data?.status === "ready") { url = r.data.file_url; break; }
        if (r.data?.status === "error") break;
        await new Promise((res) => setTimeout(res, 2500));
      }
      if (url) {
        const a = document.createElement("a");
        a.href = absUrl(url); a.download = "anniversary-montage.mp4"; a.target = "_blank"; a.rel = "noopener";
        document.body.appendChild(a); a.click(); a.remove();
      }
    } catch { /* ignore */ } finally { setSavingVideo(false); }
  };

  // Save ALL the photos. On phones we use the native share sheet (lets you
  // tap "Save N Images" straight to the camera roll); otherwise we download a
  // single .zip of every photo. (Looping individual downloads only ever saved
  // one file on iOS Safari.)
  const savePhotos = async () => {
    if (!photos.length || savingPhotos) return;
    setSavingPhotos(true);
    try {
      // Try the native share sheet with every photo as a file.
      try {
        const files = [];
        for (let i = 0; i < photos.length; i++) {
          const resp = await fetch(absUrl(photos[i].url));
          const blob = await resp.blob();
          files.push(new File([blob], `anniversary-photo-${i + 1}.jpg`, { type: blob.type || "image/jpeg" }));
        }
        if (files.length && navigator.canShare && navigator.canShare({ files })) {
          await navigator.share({ files, title: "Anniversary photos" });
          return;
        }
      } catch (err) {
        if (err && err.name === "AbortError") return; // user closed the share sheet
        /* fall through to zip download */
      }
      // Fallback: one .zip with every photo.
      const a = document.createElement("a");
      a.href = `${API}/birthday/wall/${honoreeId}/photos/zip?date=${encodeURIComponent(date)}&kind=${encodeURIComponent(kind)}`;
      a.download = "anniversary-photos.zip"; a.target = "_blank"; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
    } finally { setSavingPhotos(false); }
  };



  const toggleSticker = (s) =>
    setStickers((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : cur.length < 6 ? [...cur, s] : cur));

  const submit = async () => {
    if (!msg.trim() && stickers.length === 0) return;
    setSending(true);
    try {
      await axios.post(`${API}/birthday/wall/${honoreeId}/sign`, {
        actor_id: crewId,
        message: msg.trim(),
        visibility,
        stickers,
        date,
        kind,
      });
      setMsg(""); setStickers([]); setShowSign(false);
      await load();
    } catch {
      /* ignore */
    } finally {
      setSending(false);
    }
  };

  const honoree = data?.honoree;
  const isHonoree = data?.is_honoree;

  const saveNameSpelling = async () => {
    const spelling = (nameDraft || "").trim();
    setSavingName(true);
    try {
      await axios.post(`${API}/birthday/name-spelling`, { actor_id: crewId, spelling });
      setNameEditing(false);
      await load(); // re-fetch so notes auto-correct to the new spelling
    } catch {
      /* ignore */
    } finally {
      setSavingName(false);
    }
  };

  const exportCard = async () => {
    const node = captureRef.current;
    if (!node || exporting) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#c99a63",
        // Skip the confetti + any control chrome that isn't inside captureRef.
        filter: (el) => !(el.classList && el.classList.contains("bw-no-export")),
      });
      const fileName = `${(honoree?.first_name || "birthday").toLowerCase()}-${kind}-card.png`;
      // Try the native share sheet first (great on phones), else download.
      try {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], fileName, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "My birthday card 🎂" });
          setExporting(false);
          return;
        }
      } catch { /* fall through to download */ }
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = fileName;
      a.click();
    } catch {
      /* ignore */
    } finally {
      setExporting(false);
    }
  };
  const sigs = data?.signatures || [];
  const count = data?.signature_count || 0;
  const shownSigs = sigs.slice(0, visibleCount);
  const hasMoreSigs = sigs.length > visibleCount;

  // Reveal signatures a batch at a time as the honoree scrolls down the wall,
  // instead of mounting every polaroid at once (user ask 2026-06-21: "the
  // signatures load all at once rather than when you scroll"). Also keeps the
  // initial open snappy on big cards. IntersectionObserver watches a sentinel
  // just below the currently-shown notes.
  useEffect(() => {
    if (!hasMoreSigs) return undefined;
    const el = moreRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((n) => n + 6);
        }
      },
      { rootMargin: "200px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMoreSigs, shownSigs.length]);
  const alreadySigned = data?.already_signed;
  const heroAvatar = useMemo(() => absUrl(honoree?.avatar_url), [honoree]);
  const effYears = years || data?.years || 0;

  return (
    <div className={`bw-root ${cleanView ? "bw-clean" : ""}`} data-testid="birthday-wall">
      <style>{BW_CSS}</style>
      <div className="bw-confetti-host" ref={confettiHost} />

      <button className="bw-close bw-no-export" onClick={onClose} data-testid="birthday-wall-close" aria-label="Close">
        <XIcon weight="bold" className="w-5 h-5" />
      </button>

      <button className="bw-tidy bw-no-export" onClick={() => setCleanView(true)} data-testid="birthday-tidy" aria-label="Tidy for screenshot">
        <Camera weight="fill" /> <span>Tidy for photo</span>
      </button>

      {cleanView && (
        <>
          <div className="bw-clean-tap" onClick={() => setCleanView(false)} data-testid="birthday-tidy-exit" />
          <div className="bw-clean-hint">📸 Take your screenshot — tap anywhere to exit</div>
        </>
      )}

      <div ref={captureRef} className="bw-capture">
      {/* HERO */}
      <div className="bw-hero">
        <div className="bw-eyebrow">{kind === "anniversary" ? "🏆 Work Anniversary 🏆" : "🎉 Today at your store 🎉"}</div>
        <div className="bw-avatar">
          <div className="bw-crown">{kind === "anniversary" ? "🏆" : "👑"}</div>
          {heroAvatar ? <img src={heroAvatar} alt={honoree?.first_name} /> : <span>{kind === "anniversary" ? "🏆" : "🎂"}</span>}
        </div>
        {kind === "anniversary" ? (
          <h1>{effYears} {effYears === 1 ? "Year" : "Years"},<br />{honoree?.first_name || honoreeName || ""}! 🎉</h1>
        ) : (
          <h1>Happy Birthday,<br />{honoree?.first_name || honoreeName || ""}!</h1>
        )}
        <div className="bw-sub bw-no-export">sign the card below 👇</div>
        <div className="bw-counter" data-testid="birthday-wall-counter">
          🎈 <b>{count}</b>&nbsp;{count === 1 ? "teammate has" : "teammates have"} signed
        </div>
      </div>

      {/* JOURNEY SLIDESHOW (anniversary only) — how it started → now */}
      {isAnniv && (
        <div className="bw-journey" data-testid="birthday-journey">
          <div className="bw-journey-title">📸 How it started → now</div>
          {photos.length > 0 ? (
            <div
              className="bw-slideshow"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              {photos.map((p, i) => (
                <div key={p.id} className={`bw-slide ${i === slide ? "on" : ""} ${i % 2 ? "kb-out" : "kb-in"}`}>
                  {near(i, slide, photos.length) && (
                    <SlidePhoto thumb={p.thumb} full={p.url} alt={`Moment ${i + 1}`}
                      onReady={() => markLoaded(p.id)} testId="birthday-journey-photo" />
                  )}
                  {canRemovePhoto(p) && i === slide && (
                    <button className="bw-slide-del bw-no-export" onClick={() => removePhoto(p.id)} data-testid="birthday-journey-delete" aria-label="Remove photo">
                      <XIcon weight="bold" />
                    </button>
                  )}
                </div>
              ))}
              {photos[slide] && !loaded.has(photos[slide].id) && (
                <div className="bw-slide-spin" data-testid="birthday-journey-loading"><span /></div>
              )}
              {photos.length > 1 && (
                <>
                  <button className="bw-slide-nav left bw-no-export" onClick={() => goTo(slide - 1)} data-testid="birthday-journey-prev" aria-label="Previous photo">‹</button>
                  <button className="bw-slide-nav right bw-no-export" onClick={() => goTo(slide + 1)} data-testid="birthday-journey-next" aria-label="Next photo">›</button>
                  {photos.length > 12 ? (
                    <div className="bw-count bw-no-export" data-testid="birthday-journey-count">{slide + 1} / {photos.length}</div>
                  ) : (
                    <div className="bw-dots bw-no-export">
                      {photos.map((p, i) => (
                        <span key={p.id} className={`bw-dot ${i === slide ? "on" : ""}`} onClick={() => goTo(i)} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="bw-journey-empty">No photos yet — add a few to tell the story 📷</div>
          )}
          <div className="bw-journey-actions bw-no-export">
            <button
              className="bw-journey-btn primary"
              onClick={openPlay}
              disabled={photos.length === 0}
              data-testid="birthday-journey-play"
            >
              <Play weight="fill" /> Play montage
            </button>
            <button
              className="bw-journey-btn"
              onClick={() => photoInputRef.current?.click()}
              disabled={uploadingPhoto}
              data-testid="birthday-journey-add"
            >
              <Camera weight="fill" /> {uploadingPhoto ? "Uploading…" : "Add photos"}
            </button>
            <button
              className="bw-journey-btn"
              onClick={saveVideo}
              disabled={photos.length === 0 || savingVideo}
              data-testid="birthday-journey-savevideo"
            >
              <DownloadSimple weight="bold" /> {savingVideo ? "Rendering…" : "Save video"}
            </button>
            <button
              className="bw-journey-btn"
              onClick={savePhotos}
              disabled={photos.length === 0 || savingPhotos}
              data-testid="birthday-journey-savephotos"
            >
              <DownloadSimple weight="bold" /> {savingPhotos ? "Saving…" : "Save all photos"}
            </button>
            {isHonoree && (
              <button
                className="bw-journey-btn primary"
                onClick={exportCard}
                disabled={exporting}
                data-testid="birthday-journey-savecard"
              >
                <DownloadSimple weight="fill" /> {exporting ? "Saving…" : "Save my card"}
              </button>
            )}
          </div>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => addPhotos(e.target.files)}
            data-testid="birthday-journey-input"
          />
        </div>
      )}

      {/* FULL-SCREEN CINEMATIC MONTAGE */}
      {playing && (
        <div className="bw-play" onClick={closePlay} data-testid="birthday-montage-play">
          {photos.map((p, i) => (
            <div key={p.id} className={`bw-play-slide ${i === playIdx ? "on" : ""}`}>
              {near(i, playIdx, photos.length) && (
                <SlidePhoto thumb={p.thumb} full={p.url} alt={`Moment ${i + 1}`}
                  onReady={() => markLoaded(p.id)} />
              )}
            </div>
          ))}
          <div className="bw-play-grad" />
          <div className="bw-play-title">
            {effYears} {effYears === 1 ? "Year" : "Years"}, {honoree?.first_name || honoreeName || ""} 🎉
          </div>
          {photos.length > 12 ? (
            <div className="bw-play-count">{playIdx + 1} / {photos.length}</div>
          ) : (
            <div className="bw-play-dots">
              {photos.map((p, i) => <span key={p.id} className={`bw-dot ${i === playIdx ? "on" : ""}`} />)}
            </div>
          )}
          <button className="bw-play-close" onClick={closePlay} data-testid="birthday-montage-close" aria-label="Close montage">
            <XIcon weight="bold" />
          </button>
          <audio ref={audioRef} src="/sounds/montage.m4a" loop preload="auto" />
        </div>
      )}

      {/* WALL */}
      <div className="bw-wall">
        {loading && <div className="bw-empty">Loading the card…</div>}

        {!loading && isHonoree && (
          <div className="bw-owner bw-no-export">This is <b>your</b> card 🎂 — scroll to read what the crew wrote (private notes are just for you 🔒).</div>
        )}

        {!loading && isHonoree && (
          <div className="bw-namefix bw-no-export" data-testid="birthday-namefix">
            {nameEditing ? (
              <div className="bw-namefix-edit">
                <input
                  type="text"
                  value={nameDraft}
                  maxLength={40}
                  autoFocus
                  onChange={(e) => setNameDraft(e.target.value)}
                  placeholder="Spell your name exactly"
                  className="bw-namefix-input"
                  data-testid="birthday-namefix-input"
                  onKeyDown={(e) => { if (e.key === "Enter") saveNameSpelling(); }}
                />
                <button
                  className="bw-namefix-save"
                  onClick={saveNameSpelling}
                  disabled={savingName}
                  data-testid="birthday-namefix-save"
                >
                  {savingName ? "Saving…" : "Save"}
                </button>
                <button className="bw-namefix-cancel" onClick={() => setNameEditing(false)} data-testid="birthday-namefix-cancel">
                  Cancel
                </button>
              </div>
            ) : (
              <button
                className="bw-namefix-open"
                onClick={() => { setNameDraft(honoree?.name_spelling || honoree?.first_name || ""); setNameEditing(true); }}
                data-testid="birthday-namefix-open"
              >
                ✏️ {honoree?.name_spelling ? "Edit your name spelling" : "Fix your name spelling"}
              </button>
            )}
          </div>
        )}

        {!loading && sigs.length === 0 && (
          <div className="bw-empty">No signatures yet — be the first to sign! ✍️</div>
        )}

        {shownSigs.map((s, idx) => {
          const tint = pick(s.from_id, NOTE_TINTS);
          const ink = pick(s.from_id, INK);
          const tilt = tiltFor(s.from_id);
          const hidden = s.visibility === "private" && s.message === null;
          const avatar = absUrl(s.from_avatar);
          return (
            <div key={s.id} className={`bw-note ${tint}`} style={{ transform: `rotate(${tilt}deg)` }} data-testid="birthday-signature">
              <div className="bw-tape" />
              {s.belated && <span className="bw-belated" data-testid="birthday-belated-badge">🎈 Belated</span>}
              {hidden ? (
                <>
                  <div className="bw-lock">🔒 {s.from_name} signed the card</div>
                  <span className="bw-privpill">PRIVATE · ONLY {honoree?.first_name?.toUpperCase() || "THEY"} CAN READ</span>
                </>
              ) : (
                <>
                  {(s.stickers || []).length > 0 && (
                    <div className="bw-stickers">{s.stickers.join(" ")}</div>
                  )}
                  {avatar && (
                    <div className="bw-polaroid" style={{ transform: `rotate(${(-tilt / 1.4)}deg)` }}>
                      <div className="bw-ptape" />
                      <img src={avatar} alt={s.from_name} loading="lazy" decoding="async" />
                      <div className="bw-cap">{s.from_name}</div>
                    </div>
                  )}
                  {s.message && <div className="bw-msg">{s.message}</div>}
                  {s.visibility === "private" && (
                    <span className="bw-privpill you">🔒 private — only you see this</span>
                  )}
                </>
              )}
              <div className="bw-sig">
                <div className={`bw-name ${ink}`}>
                  <span className="bw-ink" style={{ animationDelay: `${0.25 + Math.min(idx, 10) * 0.14}s` }}>{s.from_name}</span>
                </div>
                <div className="bw-role">{s.from_role}</div>
              </div>
            </div>
          );
        })}

        {/* Scroll sentinel — reveals the next batch of signatures as you scroll */}
        {hasMoreSigs && (
          <div ref={moreRef} className="bw-more" data-testid="birthday-wall-more">
            <span className="bw-more-dot" />
            <span className="bw-more-dot" />
            <span className="bw-more-dot" />
          </div>
        )}

        {/* Sign panel — hidden for the honoree */}
        {!loading && !isHonoree && (
          showSign ? (
            <div className="bw-signcard" data-testid="birthday-sign-panel">
              <h3>✍️ Sign {honoree?.first_name}&rsquo;s card</h3>
              <textarea
                className="bw-signbox"
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder={`Write something for ${honoree?.first_name || "them"}…`}
                maxLength={600}
                rows={3}
                data-testid="birthday-sign-input"
              />
              <div className="bw-tray">
                {STICKER_TRAY.map((s) => (
                  <button key={s} className={`bw-chip ${stickers.includes(s) ? "on" : ""}`} onClick={() => toggleSticker(s)} type="button">{s}</button>
                ))}
              </div>
              <div className="bw-avnote"><Camera weight="fill" className="w-4 h-4" /> your profile photo drops on automatically</div>
              <div className="bw-toggle">
                <button className={`bw-tg ${visibility === "public" ? "on pub" : ""}`} onClick={() => setVisibility("public")} type="button" data-testid="birthday-vis-public">📢 Post to wall</button>
                <button className={`bw-tg ${visibility === "private" ? "on pri" : ""}`} onClick={() => setVisibility("private")} type="button" data-testid="birthday-vis-private">🔒 Private to {honoree?.first_name}</button>
              </div>
              <button className="bw-submit" onClick={submit} disabled={sending || (!msg.trim() && stickers.length === 0)} data-testid="birthday-sign-submit">
                {sending ? "Signing…" : "Sign the card 🎉"}
              </button>
            </div>
          ) : (
            <div style={{ textAlign: "center", paddingBottom: 90 }}>
              <p className="bw-thanks">{alreadySigned ? "You signed 💛 — tap to edit your note" : ""}</p>
            </div>
          )
        )}
      </div>
      </div>{/* /bw-capture */}

      {/* Honoree: save/share the finished card as an image.
          Anniversary honorees get this in the journey actions row instead, so
          only birthdays show the inline button here (no floating overlap). */}
      {isHonoree && !isAnniv && (
        <div className="bw-save-row bw-no-export">
          <button className="bw-journey-btn primary" onClick={exportCard} disabled={exporting} data-testid="birthday-export">
            <DownloadSimple weight="fill" /> {exporting ? "Saving…" : "Save my card"}
          </button>
        </div>
      )}

      {/* Floating CTA */}
      {!isHonoree && !showSign && (
        <button className="bw-fab bw-no-export" onClick={() => setShowSign(true)} data-testid="birthday-open-sign">
          <Sparkle weight="fill" className="w-5 h-5" />
          <span>{alreadySigned ? "Edit my note" : "Sign the card"}</span>
        </button>
      )}
    </div>
  );
}

const BW_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Permanent+Marker&family=Caveat:wght@600;700&display=swap');
.bw-root{ position:fixed; inset:0; z-index:9998; overflow-y:auto; -webkit-overflow-scrolling:touch;
  background:
    repeating-radial-gradient(circle at 50% 50%, rgba(0,0,0,.015) 0 2px, transparent 2px 4px),
    linear-gradient(160deg,#d8b483,#c99a63 45%,#bd8a51);
  color:#1e293b; }
.bw-confetti-host{ position:fixed; inset:0; pointer-events:none; z-index:9999; overflow:hidden; }
.bw-confetti{ position:absolute; top:-14px; width:9px; height:14px; opacity:.9; animation:bwfall linear forwards; }
@keyframes bwfall{ to{ transform:translateY(110vh) rotate(720deg); } }
.bw-close{ position:fixed; top:calc(14px + env(safe-area-inset-top)); right:calc(14px + env(safe-area-inset-right)); z-index:10001; width:38px; height:38px; border-radius:50%;
  background:rgba(15,23,42,.55); color:#fff; display:flex; align-items:center; justify-content:center; border:none; backdrop-filter:blur(6px); }
.bw-tidy{ position:fixed; top:calc(14px + env(safe-area-inset-top)); left:calc(14px + env(safe-area-inset-left)); z-index:10001;
  display:flex; align-items:center; gap:6px; padding:8px 14px; border-radius:999px; border:none; cursor:pointer;
  background:rgba(15,23,42,.55); color:#fff; font-weight:800; font-size:12px; backdrop-filter:blur(6px); }
.bw-tidy svg{ width:15px; height:15px; }
.bw-clean .bw-no-export{ display:none !important; }
.bw-clean-tap{ position:fixed; inset:0; z-index:12000; background:transparent; }
.bw-clean-hint{ position:fixed; top:calc(10px + env(safe-area-inset-top)); left:50%; transform:translateX(-50%); z-index:12001;
  background:rgba(15,23,42,.82); color:#fff; font-weight:700; font-size:12px; padding:8px 16px; border-radius:999px;
  backdrop-filter:blur(6px); white-space:nowrap; pointer-events:none; animation:bwhint 3.4s ease forwards; }
@keyframes bwhint{ 0%{opacity:0;} 10%{opacity:1;} 68%{opacity:1;} 100%{opacity:0;} }
.bw-hero{ position:relative; padding:calc(26px + env(safe-area-inset-top)) 20px 30px; text-align:center; overflow:hidden;
  background:linear-gradient(150deg,#5b21b6,#7c3aed 45%,#db2777);
  border-bottom-left-radius:26px; border-bottom-right-radius:26px; box-shadow:0 10px 30px rgba(91,33,182,.4); }
.bw-eyebrow{ font-family:'Permanent Marker',cursive; color:#fde68a; letter-spacing:1px; font-size:14px; }
.bw-hero h1{ font-family:'Permanent Marker',cursive; color:#fff; font-size:30px; line-height:1.05; margin:6px 0 4px; text-shadow:0 3px 10px rgba(0,0,0,.35); }
.bw-sub{ color:#f5d0fe; font-size:13px; font-weight:700; }
.bw-avatar{ width:96px; height:96px; border-radius:50%; margin:14px auto 10px; border:5px solid #fff;
  background:rgba(255,255,255,.16); display:flex; align-items:center; justify-content:center; position:relative;
  box-shadow:0 8px 22px rgba(0,0,0,.3); font-size:34px; color:#fff; }
.bw-avatar img{ width:100%; height:100%; border-radius:50%; object-fit:cover; }
.bw-crown{ position:absolute; top:-22px; left:50%; transform:translateX(-50%) rotate(-8deg); font-size:30px; }
.bw-counter{ display:inline-flex; align-items:center; margin-top:8px; background:rgba(255,255,255,.2); backdrop-filter:blur(6px);
  padding:7px 15px; border-radius:999px; color:#fff; font-weight:900; font-size:13px; border:1px solid rgba(255,255,255,.3); }
.bw-counter b{ font-size:15px; }
.bw-wall{ padding:22px 12px 130px; position:relative; }
.bw-owner{ max-width:320px; margin:0 auto 20px; background:rgba(255,255,255,.9); border-radius:14px; padding:11px 14px; font-size:13px; font-weight:700; color:#7c2d12; text-align:center; box-shadow:0 6px 16px rgba(0,0,0,.15); }
.bw-namefix{ max-width:320px; margin:-8px auto 20px; text-align:center; }
.bw-namefix-open{ background:rgba(255,255,255,.75); border:1px dashed rgba(124,45,18,.4); color:#7c2d12; font-size:12px; font-weight:800; border-radius:999px; padding:6px 14px; cursor:pointer; transition:background .15s ease, transform .1s ease; }
.bw-namefix-open:hover{ background:#fff; }
.bw-namefix-open:active{ transform:scale(.96); }
.bw-namefix-edit{ display:flex; gap:6px; align-items:center; justify-content:center; flex-wrap:wrap; background:rgba(255,255,255,.92); border-radius:12px; padding:8px; box-shadow:0 6px 16px rgba(0,0,0,.15); }
.bw-namefix-input{ flex:1; min-width:140px; border:1px solid #d6bfa6; border-radius:9px; padding:7px 10px; font-size:14px; font-weight:700; color:#3f2a15; outline:none; }
.bw-namefix-input:focus{ border-color:#a855f7; box-shadow:0 0 0 2px rgba(168,85,247,.25); }
.bw-namefix-save{ background:#a855f7; color:#fff; font-weight:800; font-size:12px; border-radius:9px; padding:7px 14px; cursor:pointer; }
.bw-namefix-save:disabled{ opacity:.6; cursor:default; }
.bw-namefix-cancel{ background:transparent; color:#7c2d12; font-weight:700; font-size:12px; padding:7px 8px; cursor:pointer; }

.bw-empty{ text-align:center; color:#7c2d12; font-family:'Caveat',cursive; font-size:20px; font-weight:700; padding:30px 20px; }
.bw-more{ display:flex; align-items:center; justify-content:center; gap:6px; padding:18px 0 8px; }
.bw-more-dot{ width:7px; height:7px; border-radius:999px; background:rgba(124,45,18,.45); animation:bw-more-bounce 1.2s ease-in-out infinite; }
.bw-more-dot:nth-child(2){ animation-delay:.15s; }
.bw-more-dot:nth-child(3){ animation-delay:.3s; }
@keyframes bw-more-bounce{ 0%,80%,100%{ transform:translateY(0); opacity:.5; } 40%{ transform:translateY(-5px); opacity:1; } }

.bw-note{ position:relative; margin:0 auto 30px; width:290px; max-width:88%; background:#fffdf5; border-radius:4px; padding:18px 18px 14px; box-shadow:0 10px 22px rgba(0,0,0,.28); }
.bw-note.y{ background:#fef9c3; } .bw-note.p{ background:#fce7f3; } .bw-note.b{ background:#dbeafe; } .bw-note.g{ background:#dcfce7; } .bw-note.o{ background:#ffedd5; } .bw-note.w{ background:#fffdf5; }
.bw-tape{ position:absolute; top:-13px; left:50%; transform:translateX(-50%) rotate(-3deg); width:96px; height:26px; opacity:.72;
  background:repeating-linear-gradient(45deg,rgba(255,255,255,.5) 0 6px,rgba(255,255,255,.15) 6px 12px), linear-gradient(90deg,#67e8f9,#22d3ee); box-shadow:0 2px 5px rgba(0,0,0,.12); }
.bw-note.y .bw-tape{ background:repeating-linear-gradient(45deg,rgba(255,255,255,.5) 0 6px,rgba(255,255,255,.15) 6px 12px), linear-gradient(90deg,#fca5a5,#f87171); }
.bw-note.p .bw-tape{ background:repeating-linear-gradient(45deg,rgba(255,255,255,.5) 0 6px,rgba(255,255,255,.15) 6px 12px), linear-gradient(90deg,#c4b5fd,#a78bfa); }
.bw-note.o .bw-tape{ background:repeating-linear-gradient(45deg,rgba(255,255,255,.5) 0 6px,rgba(255,255,255,.15) 6px 12px), linear-gradient(90deg,#fdba74,#fb923c); }
.bw-note.g .bw-tape{ background:repeating-linear-gradient(45deg,rgba(255,255,255,.5) 0 6px,rgba(255,255,255,.15) 6px 12px), linear-gradient(90deg,#f9a8d4,#f472b6); }
.bw-stickers{ font-size:20px; letter-spacing:2px; margin-bottom:6px; }
.bw-msg{ font-family:'Caveat',cursive; font-size:23px; font-weight:600; line-height:1.15; color:#334155; margin-bottom:12px; }
.bw-polaroid{ background:#fff; padding:8px 8px 26px; width:140px; margin:0 auto 12px; box-shadow:0 6px 16px rgba(0,0,0,.25); position:relative; }
.bw-polaroid img{ width:100%; height:120px; object-fit:cover; display:block; background:#eee; }
.bw-ptape{ position:absolute; top:-10px; left:50%; transform:translateX(-50%) rotate(6deg); width:66px; height:18px; background:linear-gradient(90deg,#fde68a,#fbbf24); opacity:.7; }
.bw-cap{ font-family:'Caveat',cursive; font-weight:700; font-size:16px; color:#475569; text-align:center; position:absolute; bottom:5px; left:0; right:0; }
.bw-sig{ display:flex; align-items:flex-end; justify-content:space-between; gap:8px; border-top:1px dashed rgba(0,0,0,.12); padding-top:8px; }
.bw-name{ font-family:'Permanent Marker',cursive; font-size:26px; line-height:.9; transform:rotate(-3deg); }
.bw-name .bw-ink{ display:inline-block; clip-path:inset(0 100% -15% 0); animation:bwink 1.05s cubic-bezier(.7,0,.3,1) both; }
@keyframes bwink{ to{ clip-path:inset(0 -10% -15% 0); } }
@media (prefers-reduced-motion: reduce){ .bw-name .bw-ink{ animation:none; clip-path:none; } }
.bw-name.c1{ color:#1d4ed8; } .bw-name.c2{ color:#be123c; } .bw-name.c3{ color:#7e22ce; } .bw-name.c4{ color:#047857; } .bw-name.c5{ color:#c2410c; } .bw-name.c6{ color:#0f766e; }
.bw-role{ font-size:9.5px; font-weight:900; text-transform:uppercase; letter-spacing:.4px; color:#94a3b8; text-align:right; flex:none; }
.bw-lock{ font-family:'Caveat',cursive; font-size:20px; color:#475569; font-weight:700; margin-bottom:8px; }
.bw-privpill{ display:inline-block; font-size:10px; font-weight:900; background:#e2e8f0; color:#475569; padding:3px 8px; border-radius:999px; letter-spacing:.4px; }
.bw-belated{ position:absolute; top:8px; right:8px; font-size:9px; font-weight:900; background:#fce7f3; color:#be185d; padding:3px 8px; border-radius:999px; letter-spacing:.3px; box-shadow:0 1px 3px rgba(0,0,0,.1); }

.bw-privpill.you{ background:#fef3c7; color:#b45309; margin-top:4px; }
.bw-signcard{ width:300px; max-width:92%; margin:0 auto 26px; background:#fff; border-radius:20px; padding:16px; box-shadow:0 14px 34px rgba(0,0,0,.3); }
.bw-signcard h3{ font-family:'Permanent Marker',cursive; color:#7c3aed; font-size:18px; margin-bottom:8px; }
.bw-signbox{ width:100%; background:#faf5ff; border:1.5px solid #e9d5ff; border-radius:12px; padding:10px; font-family:'Caveat',cursive; font-size:19px; color:#6b21a8; resize:none; outline:none; }
.bw-tray{ display:flex; flex-wrap:wrap; gap:6px; margin-top:10px; }
.bw-chip{ font-size:19px; width:38px; height:38px; border-radius:10px; border:1.5px solid #e2e8f0; background:#f8fafc; }
.bw-chip.on{ background:#ede9fe; border-color:#a78bfa; }
.bw-avnote{ display:flex; align-items:center; gap:6px; font-size:11px; color:#64748b; font-weight:700; margin-top:10px; }
.bw-toggle{ display:flex; gap:8px; margin-top:10px; }
.bw-tg{ flex:1; text-align:center; font-size:11.5px; font-weight:900; padding:10px 6px; border-radius:11px; border:1.5px solid #e2e8f0; background:#f8fafc; color:#475569; }
.bw-tg.on.pub{ background:#ede9fe; color:#6d28d9; border-color:#c4b5fd; }
.bw-tg.on.pri{ background:#f1f5f9; color:#0f172a; border-color:#94a3b8; }
.bw-submit{ margin-top:12px; width:100%; background:linear-gradient(135deg,#7c3aed,#db2777); color:#fff; font-weight:900; font-size:15px; border:none; border-radius:14px; padding:13px; box-shadow:0 8px 18px rgba(124,58,237,.4); }
.bw-submit:disabled{ opacity:.5; }
.bw-thanks{ font-family:'Caveat',cursive; font-size:18px; font-weight:700; color:#7c2d12; }
.bw-fab{ position:fixed; bottom:calc(18px + env(safe-area-inset-bottom)); left:50%; transform:translateX(-50%); z-index:10001; display:flex; align-items:center; gap:8px;
  background:linear-gradient(135deg,#7c3aed,#db2777); color:#fff; font-weight:900; font-size:15px; padding:14px 26px; border-radius:999px;
  box-shadow:0 12px 28px rgba(219,39,119,.5); border:2px solid rgba(255,255,255,.4); max-width:400px; }
.bw-fab span{ font-family:'Permanent Marker',cursive; letter-spacing:.5px; }

/* Journey slideshow — "how it started → now" (anniversary) */
.bw-journey{ margin:16px auto 4px; max-width:460px; padding:0 16px; text-align:center; }
.bw-journey-title{ font-family:'Permanent Marker',cursive; color:#7c2d12; font-size:18px; margin-bottom:10px; }
.bw-slideshow{ position:relative; width:100%; aspect-ratio:4/3; border-radius:18px; overflow:hidden;
  background:#1f1206; box-shadow:0 14px 34px rgba(0,0,0,.35); border:5px solid #fff;
  transform:rotate(-1deg); }
.bw-slide{ position:absolute; inset:0; opacity:0; pointer-events:none; transition:opacity .6s ease; }
.bw-slide.on{ opacity:1; pointer-events:auto; }
.bw-slide-spin{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center; z-index:1; }
.bw-slide-spin span{ width:30px; height:30px; border-radius:50%; border:3px solid rgba(255,255,255,.35); border-top-color:#fff; animation:bwspin .8s linear infinite; }
@keyframes bwspin{ to{ transform:rotate(360deg); } }
.bw-slide img{ width:100%; height:100%; object-fit:cover; display:block; transform:scale(1.05); }
.bw-slide.on.kb-in img{ animation:bwken-in 5.5s ease-out forwards; }
.bw-slide.on.kb-out img{ animation:bwken-out 5.5s ease-out forwards; }
@keyframes bwken-in{ from{ transform:scale(1.05) translate(0,0); } to{ transform:scale(1.2) translate(-1.5%,1.5%); } }
@keyframes bwken-out{ from{ transform:scale(1.2) translate(1.5%,-1.5%); } to{ transform:scale(1.05) translate(0,0); } }
.bw-slide-del{ position:absolute; top:8px; right:8px; width:30px; height:30px; border-radius:50%;
  background:rgba(15,23,42,.6); color:#fff; border:none; display:flex; align-items:center; justify-content:center;
  backdrop-filter:blur(6px); cursor:pointer; }
.bw-slide-del svg{ width:15px; height:15px; }
.bw-slide-nav{ position:absolute; top:50%; transform:translateY(-50%); width:40px; height:40px; border-radius:50%;
  background:rgba(0,0,0,.42); color:#fff; border:none; font-size:26px; line-height:1; font-weight:700;
  display:flex; align-items:center; justify-content:center; cursor:pointer; backdrop-filter:blur(4px); z-index:2; }
.bw-slide-nav.left{ left:8px; } .bw-slide-nav.right{ right:8px; }
.bw-dots{ position:absolute; bottom:10px; left:0; right:0; display:flex; justify-content:center; gap:7px; z-index:2; }
.bw-dot{ width:8px; height:8px; border-radius:50%; background:rgba(255,255,255,.55); cursor:pointer; transition:all .25s; }
.bw-dot.on{ background:#fff; transform:scale(1.35); }
.bw-count{ position:absolute; bottom:10px; left:0; right:0; text-align:center; z-index:2;
  color:#fff; font-size:12px; font-weight:800; text-shadow:0 1px 4px rgba(0,0,0,.6); letter-spacing:.3px; }
.bw-play-count{ position:absolute; top:calc(22px + env(safe-area-inset-top)); left:0; right:0; text-align:center;
  color:#fff; font-size:13px; font-weight:800; text-shadow:0 1px 6px rgba(0,0,0,.7); z-index:12001; }
.bw-journey-empty{ padding:26px 14px; border-radius:16px; border:2px dashed rgba(124,45,18,.35);
  color:#7c2d12; font-weight:700; font-size:14px; background:rgba(255,255,255,.35); }
.bw-journey-actions{ margin:12px auto 0; display:flex; flex-wrap:wrap; justify-content:center; gap:8px; }
.bw-save-row{ display:flex; justify-content:center; margin:14px auto 10px; padding:0 16px; }
.bw-journey-btn{ display:inline-flex; align-items:center; gap:6px; cursor:pointer;
  background:#fff; color:#7c2d12; font-weight:800; font-size:13px; padding:9px 16px; border-radius:999px;
  border:2px solid rgba(124,45,18,.28); box-shadow:0 5px 14px rgba(0,0,0,.12); transition:transform .15s ease, box-shadow .15s ease; }
.bw-journey-btn:hover:not(:disabled){ transform:translateY(-1px); box-shadow:0 8px 18px rgba(0,0,0,.16); }
.bw-journey-btn:disabled{ opacity:.5; cursor:default; }
.bw-journey-btn svg{ width:17px; height:17px; }
.bw-journey-btn.primary{ background:linear-gradient(135deg,#7c3aed,#db2777); color:#fff; border-color:rgba(255,255,255,.4); }

/* Full-screen cinematic montage */
.bw-play{ position:fixed; inset:0; z-index:12000; background:#000; overflow:hidden; cursor:pointer; }
.bw-play-slide{ position:absolute; inset:0; opacity:0; transition:opacity 1s ease; }
.bw-play-slide.on{ opacity:1; }
.bw-play-slide img{ width:100%; height:100%; object-fit:cover; display:block; transform:scale(1.04); }
.bw-play-slide.on img{ animation:bwken 3.4s ease-out forwards; }
@keyframes bwken{ from{ transform:scale(1.04) translate(0,0); } to{ transform:scale(1.18) translate(-1.5%,-1.5%); } }
.bw-play-grad{ position:absolute; inset:0; pointer-events:none;
  background:linear-gradient(to top, rgba(0,0,0,.6), transparent 34%); }
.bw-play-title{ position:absolute; left:16px; right:16px; bottom:calc(42px + env(safe-area-inset-bottom));
  text-align:center; color:#fff; font-family:'Permanent Marker',cursive; font-size:26px; line-height:1.1;
  text-shadow:0 3px 16px rgba(0,0,0,.7); pointer-events:none; }
.bw-play-dots{ position:absolute; top:calc(20px + env(safe-area-inset-top)); left:0; right:0;
  display:flex; justify-content:center; gap:6px; z-index:12001; }
.bw-play-close{ position:fixed; top:calc(14px + env(safe-area-inset-top)); right:calc(14px + env(safe-area-inset-right));
  z-index:12002; width:40px; height:40px; border-radius:50%; background:rgba(255,255,255,.22); color:#fff;
  border:none; backdrop-filter:blur(6px); display:flex; align-items:center; justify-content:center; cursor:pointer; }
.bw-play-close svg{ width:18px; height:18px; }

`;
